import { Alarm, Clear } from '@mui/icons-material';
import { Divider, FormGroup, FormLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useFormFields } from 'libs/hooksLib';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';

import CustomButton from 'common/components/CustomButton';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomTextValidator from 'common/components/CustomTextValidators';
import useFlexBoxStyles from 'common/flexBox';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    h5: {
        height: theme.spacing(5),
    },
    padding0: {
        '& .MuiInputBase-root': {
            padding: 0,
        },
    },
    eventInputSelector: {
        margin: theme.spacing(0.5),
        backgroundColor: 'none',
        '& > :first-of-type': {
            fontSize: theme.typography.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            width: 'auto',
            borderRadius: '0 !important',
            borderLeft: `1px solid ${theme.palette.secondary.light} !important`,
            '& > fieldset': {
                border: 'none !important',
            },
        },
        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
            overflow: 'hidden',
        },
        '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: theme.palette.default.main,
        },
        '& input': {
            position: 'absolute !important',
        },
    },
}));

const AppointmentFormReminders = (props) => {
    const { reminders, setReminders } = props;
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();

    const [fields, handleFieldChange] = useFormFields({
        time: 0,
        unit: 'h',
        direction: 'before'
    });

    const handleAddReminderSubmit = (e) => {
        e.preventDefault();
        setReminders([...reminders, fields]);
    };

    const deleteReminder = (index) => {
        if (index >= 0 && index < reminders.length) {
            setReminders([...reminders.slice(0, index), ...reminders.slice(index + 1, reminders.length)]);
        }
    };

    const onTimeChange = (e) => {
        if (e.target.value >= 0) {
            handleFieldChange(e);
        }
    };

    return (
        <Stack direction="column" spacing={1}>
            <ValidatorForm
                onSubmit={handleAddReminderSubmit}
                onError={() => { }}
                className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
            >
                <FormGroup>
                    <FormLabel>{t('iCallendar.appointmenForm.reminder')}</FormLabel>

                    <Stack direction="row" justifyContent="space-between" className={cx(flexBox.w100)}>
                        <div className={cx(flexBox.w75, flexBox.marginRight2)}>
                            <CustomTextValidator
                                name="time"
                                fullWidth={true}
                                variant="outlined"
                                size="small"
                                type={'number'}
                                min={0}
                                useRegExpText={false}
                                value={fields.time}
                                onChange={(e) => onTimeChange(e)}
                                validators={[`minNumber:0`, 'required']}
                                errorMessages={[t('event.eventFormReminders.minValue'), t('app.common.required')]}
                                InputProps={{
                                    endAdornment: (
                                        <FormGroup>
                                            <TextField
                                                name="unit"
                                                select
                                                className={classes.eventInputSelector}
                                                value={fields.unit}
                                                onChange={handleFieldChange}
                                                variant="outlined"
                                                size="small"
                                            >
                                                <MenuItem key="hour" value="h">
                                                    {t('event.eventFormReminders.hours').toUpperCase()}
                                                </MenuItem>
                                                <MenuItem key="minute" value="m">
                                                    {t('event.eventFormReminders.minutes').toUpperCase()}
                                                </MenuItem>
                                                <MenuItem key="second" value="s">
                                                    {t('event.eventFormReminders.seconds').toUpperCase()}
                                                </MenuItem>
                                            </TextField>
                                        </FormGroup>
                                    ),
                                }}
                            />
                        </div>

                        <CustomButton variant="contained" color="primary" type="submit">
                            <span>{t('event.eventFormActors.add')}</span>
                        </CustomButton>
                    </Stack>
                </FormGroup>
            </ValidatorForm>

            {reminders?.map((reminder, index) => {
                return (
                    <Stack key={`reminder-${index}`} direction="column" spacing={1}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            className={flexBox.overflowHidden}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                className={cx(flexBox.overflowHidden, flexBox.w70, classes.h5)}
                            >
                                <Alarm />

                                <Stack direction="column" className={flexBox.overflowHidden}>
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        {`${reminder.time}${reminder.unit} before`}
                                    </Typography>
                                </Stack>
                            </Stack>

                            <CustomIconButton size="small" onClick={() => deleteReminder(index)}>
                                <Clear />
                            </CustomIconButton>
                        </Stack>

                        {index + 1 < reminders.length && <Divider />}
                    </Stack>
                );
            })}
        </Stack>
    );
};

export default AppointmentFormReminders;
